import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { AlertTriangle, CheckCircle2, Clock, Minus, Plus, Star, UserRound, Users } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api, post } from "../lib/api";
import { CategoryPill } from "./Categories";
import { dueLabel, isMustDo, PRIORITY_LABEL, type Item, type Person } from "../lib/work";

const TONE: Record<string, { pill: string; dot: string }> = {
  open: { pill: "bg-slate-100 text-slate-700 ring-slate-200", dot: "bg-slate-400" },
  in_progress: { pill: "bg-brand-50 text-brand-700 ring-brand-100", dot: "bg-brand-500" },
  waiting: { pill: "bg-amber-50 text-amber-800 ring-amber-200", dot: "bg-amber-500" },
  done: { pill: "bg-emerald-50 text-emerald-700 ring-emerald-200", dot: "bg-emerald-500" },
  cancelled: { pill: "bg-zinc-100 text-zinc-500 ring-zinc-200", dot: "bg-zinc-400" },
};

export function StatusPill({ state, label, pending }: { state: string; label?: string; pending?: boolean }) {
  if (pending) {
    return <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-2 py-0.5 text-xs font-semibold text-violet-700 ring-1 ring-inset ring-violet-200"><span className="h-1.5 w-1.5 rounded-full bg-violet-500" />Awaiting sign-off</span>;
  }
  const t = TONE[state] ?? TONE.open!;
  const text = label ?? state.replace("_", " ");
  return (
    <span className={clsx("inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold capitalize ring-1 ring-inset", t.pill)}>
      <span className={clsx("h-1.5 w-1.5 rounded-full", t.dot)} />
      {text}
    </span>
  );
}

export function PriorityBadge({ p }: { p: string }) {
  if (p === "P3" || p === "P4") return null;
  return (
    <span className={clsx("badge uppercase", p === "P1" ? "bg-red-50 text-red-700 ring-1 ring-inset ring-red-200" : "bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-200")}>
      {PRIORITY_LABEL[p]}
    </span>
  );
}

export function Due({ at }: { at: string | null }) {
  const d = dueLabel(at);
  if (d.tone === "none") return null;
  return (
    <span className={clsx("inline-flex items-center gap-1 text-xs font-medium", d.tone === "late" ? "text-red-600" : d.tone === "soon" ? "text-amber-700" : "muted")}>
      <Clock size={12} strokeWidth={2.25} /> {d.text}
    </span>
  );
}

const Sep = () => <span className="h-1 w-1 rounded-full bg-[var(--line-strong)]" aria-hidden />;

const closedState = (s: string) => s === "done" || s === "cancelled";

/** The circle at the start of a row: press it to finish the task (Liam, 31 Oct), it strikes through and turns green. */
function TickCircle({ i }: { i: Item }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const done = i.state === "done";
  const finish = async (e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    if (done || busy) return;
    setBusy(true);
    try {
      await post(`/api/work/items/${i.id}/logs`, { outcome: "done" });
      for (const k of ["items", "item", "planner-mine-tasks", "planner-prepare", "my-day", "plan-today", "triage", "came-in", "week"]) void qc.invalidateQueries({ queryKey: [k] });
    } catch (err) { alert(err instanceof Error ? err.message : "Couldn't finish it"); } finally { setBusy(false); }
  };
  return (
    <button type="button" className="mt-[3px] shrink-0" aria-label={done ? "Done" : `Finish ${i.title}`} title={done ? "Done" : "Mark done"} onClick={finish} disabled={done || busy}>
      {done ? (
        <CheckCircle2 size={18} className="text-emerald-500" />
      ) : i.slaState === "breached" ? (
        <AlertTriangle size={18} className="text-red-500 hover:text-emerald-500" />
      ) : (
        <span className={clsx("block h-[18px] w-[18px] rounded-full border-2 transition-colors hover:border-emerald-500 hover:bg-emerald-50", busy ? "border-emerald-400" : "border-[#c9cede] group-hover:border-brand-400")} />
      )}
    </button>
  );
}

export function ItemRow({ i, extra, subOpen, onSubToggle, showOwner }: { i: Item & { spaceName?: string; statusLabel?: string; assigneeNames?: string[]; ownerName?: string | null }; showOwner?: boolean; extra?: React.ReactNode; subOpen?: boolean; onSubToggle?: () => void }) {
  const starred = isMustDo(i);
  return (
    <li>
      <Link to="/work/$id" params={{ id: i.id }} className="group flex items-start gap-3 rounded-xl px-3 py-3 transition-colors hover:bg-[#f4f5fb]">
        <TickCircle i={i} />
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {starred && <Star size={13} className="fill-amber-400 text-amber-400" aria-label="Must-do" />}
            <span className={clsx("truncate text-[0.9375rem] font-semibold group-hover:text-brand-700", closedState(i.state) ? "text-[var(--muted)] line-through" : "text-[var(--text)]")}>{i.title}</span>
            <PriorityBadge p={i.priority} />
            {i.category && <CategoryPill c={i.category} />}
          </span>
          <span className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs text-[var(--muted)]">
            <span className="font-medium num">{i.ref}</span>
            {i.spaceName && (<><Sep /><span>{i.spaceName}</span></>)}
            <StatusPill state={i.state} label={i.statusLabel} pending={i.pendingVerification} />
            <Due at={i.dueAt} />
            {showOwner && i.ownerName && <span className="inline-flex items-center gap-1 font-medium text-brand-700"><UserRound size={12} /> From {i.ownerName}</span>}
            {!showOwner && i.assigneeNames && i.assigneeNames.length > 0 && (
              <span className="inline-flex items-center gap-1"><Users size={12} /> {i.assigneeNames.join(", ")}</span>
            )}
            {i.poolTeamId && i.assigneeIds.length === 0 && <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 font-semibold text-sky-700 ring-1 ring-inset ring-sky-200">Team pool</span>}
            {i.subtasks && onSubToggle && (
              <button type="button" className={clsx("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-semibold", subOpen ? "border-brand-300 bg-brand-50 text-brand-700" : "border-[var(--line)] hover:border-brand-300")}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onSubToggle(); }} aria-expanded={subOpen} title="Show sub-tasks">
                {subOpen ? <Minus size={11} /> : <Plus size={11} />}{i.subtasks.done}/{i.subtasks.total} sub-tasks
              </button>
            )}
            {extra}
          </span>
        </span>
      </Link>
    </li>
  );
}

export function PersonPicker({ value, onChange, placeholder = "Search people…", multiple = true }: { value: Person[]; onChange: (p: Person[]) => void; placeholder?: string; multiple?: boolean }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  // the list floats above everything (fixed, in <body>): inside a dialog or a card it would otherwise be cut off
  const [pos, setPos] = useState<{ left: number; width: number; top?: number; bottom?: number; maxH: number } | null>(null);
  const res = useQuery({ queryKey: ["people-pick", q], queryFn: () => api<{ items: Person[] }>(`/api/work/people?q=${encodeURIComponent(q)}`), enabled: open });
  useEffect(() => {
    const h = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && !listRef.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = ref.current?.getBoundingClientRect();
      if (!r) return;
      const below = window.innerHeight - r.bottom - 12, above = r.top - 12;
      const up = below < 200 && above > below;
      setPos({ left: r.left, width: r.width, ...(up ? { bottom: window.innerHeight - r.top + 6 } : { top: r.bottom + 6 }), maxH: Math.max(140, Math.min(256, up ? above : below)) });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => { window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); };
  }, [open, value.length]);
  return (
    <div ref={ref} className="relative">
      <div className="input flex min-h-[2.375rem] flex-wrap items-center gap-1 !py-1">
        {value.map((p) => (
          <span key={p.id} className="chip">
            {p.name}
            <button type="button" aria-label={`Remove ${p.name}`} onClick={() => onChange(value.filter((x) => x.id !== p.id))}>×</button>
          </span>
        ))}
        {(multiple || value.length === 0) && (
          <input className="min-w-[8rem] flex-1 bg-transparent text-sm outline-none" placeholder={placeholder} value={q} onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setOpen(true); }} />
        )}
      </div>
      {open && pos && createPortal(
        <ul ref={listRef} style={{ position: "fixed", left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, maxHeight: pos.maxH }}
          className="scroll-thin z-[100] overflow-auto rounded-xl border border-[var(--line)] bg-[var(--panel)] p-1 shadow-[var(--shadow-pop)]">
          {res.data?.items.filter((p) => !value.some((v) => v.id === p.id)).map((p) => (
            <li key={p.id}>
              <button type="button" className="w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-[#f4f5fb]" onClick={() => { onChange(multiple ? [...value, p] : [p]); setQ(""); setOpen(multiple); }}>
                {p.name} <span className="muted text-xs">{p.email}</span>{p.pending && <span className="muted text-xs italic"> · not signed in yet</span>}
              </button>
            </li>
          ))}
          {res.data?.items.length === 0 && <li className="muted px-3 py-1.5 text-sm">No one found</li>}
        </ul>,
        document.body,
      )}
    </div>
  );
}

/** Pick a photo/file and upload it; returns document ids. Uses the camera on phones. */
export function UploadButton({ onUploaded, label = "Add photo", accept = "image/*", capture = true }: { onUploaded: (ids: Array<{ id: string; filename: string }>) => void; label?: string; accept?: string; capture?: boolean }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const pick = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    setErr(null);
    try {
      const out: Array<{ id: string; filename: string }> = [];
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        const res = await fetch("/api/documents", { method: "POST", body: fd, credentials: "include" });
        const body = await res.json();
        if (!res.ok) throw new Error(body.message ?? "Upload failed");
        out.push({ id: body.id, filename: body.filename });
      }
      onUploaded(out);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };
  return (
    <span>
      <input ref={input} type="file" accept={accept} multiple capture={capture ? "environment" : undefined} className="hidden" onChange={(e) => void pick(e.target.files)} />
      <button type="button" className="btn" disabled={busy} onClick={() => input.current?.click()}>{busy ? "Uploading…" : label}</button>
      {err && <span className="ml-2 text-xs text-red-600">{err}</span>}
    </span>
  );
}

export function Thumbs({ ids }: { ids: string[] }) {
  if (!ids.length) return null;
  return (
    <span className="mt-1 flex flex-wrap gap-1.5">
      {ids.map((id) => (
        <a key={id} href={`/api/documents/${id}/content`} target="_blank" rel="noreferrer">
          <img src={`/api/documents/${id}/content`} alt="attachment" className="line h-16 w-16 rounded-md border object-cover" loading="lazy" onError={(e) => ((e.target as HTMLImageElement).style.display = "none")} />
        </a>
      ))}
    </span>
  );
}

/** "1/3" sub-tasks with a small bar — on Today and in Plan my day. */
export function SubProgress({ s }: { s?: { total: number; done: number } | null }) {
  if (!s?.total) return null;
  return (
    <span className="muted inline-flex shrink-0 items-center gap-1.5 text-xs" title={`${s.done} of ${s.total} sub-tasks done`}>
      <span className="h-1.5 w-10 overflow-hidden rounded-full bg-black/[0.08]"><span className="block h-full rounded-full bg-emerald-500" style={{ width: `${(s.done / s.total) * 100}%` }} /></span>
      {s.done}/{s.total}
    </span>
  );
}
